import { useMutation, useQuery, useQueryClient } from 'react-query';
import { API_ENDPOINTS } from '@framework/utils/api-endpoints';
import http from '@framework/utils/http';
import { useUI } from '@contexts/ui.context';
import { AddressType, UpdateUserType } from '@framework/types';
import { getCurrentLocationAndLanguage } from '@/utils/location';
export const fetchUserProfile = async (userId: number) => {
  const response = await http.get(`${API_ENDPOINTS.ACCOUNTDETAIL}${userId}/`);
  return response.data;
};
export const useUserProfileQuery = () => {
  const {
    isAuthorized,
    userId
  } = useUI();
  return useQuery(['userProfile', userId], () => fetchUserProfile(userId!), {
    enabled: isAuthorized && !!userId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
export const fetchProvinces = async () => {
  const {
    location
  } = getCurrentLocationAndLanguage();
  const url = location === 'tr' ? 'v2/location/list/' : API_ENDPOINTS.ADDRESSPROVINCE;
  const response = await http.get(url);
  return response.data;
};
export const useProvincesQuery = () => {
  return useQuery('provinces', fetchProvinces, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
export const fetchDistricts = async (provinceId: number) => {
  const {
    location
  } = getCurrentLocationAndLanguage();
  const url = location === 'tr' ? `v2/location/list/?parent=${provinceId}` : `${API_ENDPOINTS.ADDRESSDISTRICTS}/${provinceId}/`;
  const response = await http.get(url);
  return response.data;
};
export const useDistrictsQuery = (provinceId: number | null) => {
  return useQuery(['districts', provinceId], () => fetchDistricts(provinceId!), {
    enabled: !!provinceId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};

// Mahalle interface and hooks
export interface Mahalle {
  id: number;
  name: string;
}
export const fetchMahalles = async (districtId: number): Promise<Mahalle[]> => {
  const {
    location
  } = getCurrentLocationAndLanguage();
  const url = location === 'tr' ? `v2/location/list/?parent=${districtId}` : `${API_ENDPOINTS.ADDRESSDISTRICTS}/${districtId}/`; // fallback or another endpoint
  const response = await http.get<Mahalle[]>(url);
  return response.data;
};
export const useMahallesQuery = (districtId: number | null) => {
  return useQuery(['mahalles', districtId], () => fetchMahalles(districtId!), {
    enabled: !!districtId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};
export const updateUserProfile = async (userId: number, input: UpdateUserType) => {
  const response = await http.patch(`${API_ENDPOINTS.ACCOUNTDETAIL}${userId}/`, input);
  return response.data;
};
export const useUpdateUserProfileMutation = () => {
  const {
    userId
  } = useUI();
  const queryClient = useQueryClient();
  return useMutation((input: UpdateUserType) => updateUserProfile(userId!, input), {
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile', userId]);
    }
  });
};
export const updateUserAddress = async (userId: number, address: AddressType) => {
  const response = await http.patch(`${API_ENDPOINTS.ACCOUNTDETAIL}${userId}/`, address);
  return response.data;
};
export const useUpdateUserAddressMutation = () => {
  const {
    userId
  } = useUI();
  const queryClient = useQueryClient();
  return useMutation((address: AddressType) => updateUserAddress(userId!, address), {
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile', userId]);
    }
  });
};
export const uploadAvatar = async (userId: number, avatarFile: File) => {
  const formData = new FormData();
  formData.append('avatar', avatarFile);
  const response = await http.patch(`${API_ENDPOINTS.ACCOUNTDETAIL}${userId}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};
export const useUploadAvatarMutation = () => {
  const {
    userId
  } = useUI();
  const queryClient = useQueryClient();
  return useMutation((avatarFile: File) => uploadAvatar(userId!, avatarFile), {
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile', userId]);
    }
  });
};
export const updateNewsletterPreference = async (userId: number, agreeNewsletter: boolean) => {
  const response = await http.patch(`${API_ENDPOINTS.ACCOUNTDETAIL}${userId}/`, {
    agreeNewsLetter: agreeNewsletter
  });
  return response.data;
};
export const useUpdateNewsletterMutation = () => {
  const {
    userId
  } = useUI();
  const queryClient = useQueryClient();
  return useMutation((agreeNewsletter: boolean) => updateNewsletterPreference(userId!, agreeNewsletter), {
    onSuccess: () => {
      queryClient.invalidateQueries(['userProfile', userId]);
    }
  });
};